import { mergeAttributes } from '@tiptap/core'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import type { DOMOutputSpec, Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { NodeView, ViewMutationRecord } from '@tiptap/pm/view'
import {
  hasAuthorTableWidth,
  htmlAttribute,
  parseColwidth,
  sanitizeAlign,
  sanitizeBorderAttr,
  sanitizeColor,
  sanitizeSize,
  sanitizeStyle,
  sanitizeTableClass,
  sanitizeValign,
  setStyleProperty,
  styleFromCell,
  styleFromTable,
  styleProperty,
} from './tableHtml'

type SizedCell = {
  colspan?: number
  colwidth?: number[] | null
}

function columnWidth(colwidth: number[] | null | undefined, index: number): number | null {
  const width = colwidth?.[index]
  return width != null && width > 0 ? width : null
}

function colGroupSpec(node: ProseMirrorNode): { spec: DOMOutputSpec, totalWidth: number, fixed: boolean } | null {
  const row = node.firstChild
  if (!row) {
    return null
  }
  const cols: DOMOutputSpec[] = []
  let totalWidth = 0
  let fixed = true
  let any = false
  for (let i = 0, col = 0; i < row.childCount; i++) {
    const { colspan, colwidth } = row.child(i).attrs as SizedCell
    const span = colspan || 1
    for (let j = 0; j < span; j++, col++) {
      const width = columnWidth(colwidth, j)
      if (width) {
        any = true
        totalWidth += width
        cols.push(['col', { style: `width: ${width}px` }])
      } else {
        fixed = false
        cols.push(['col', {}])
      }
    }
  }
  if (!any) {
    return null
  }
  return {
    spec: ['colgroup', {}, ...cols],
    totalWidth,
    fixed,
  }
}

function renderTable({ node, HTMLAttributes }: { node: ProseMirrorNode, HTMLAttributes: Record<string, unknown> }) {
  const attrs = mergeAttributes(HTMLAttributes)
  const columns = colGroupSpec(node)
  if (columns?.fixed && !hasAuthorTableWidth(attrs as { style?: string | null, width?: string | null })) {
    const style = attrs.style ? `${attrs.style}; width: ${columns.totalWidth}px` : `width: ${columns.totalWidth}px`
    attrs.style = style
  }
  const children: DOMOutputSpec[] = []
  if (columns) {
    children.push(columns.spec)
  }
  children.push(['tbody', 0])
  return ['table', attrs, ...children] as DOMOutputSpec
}

function setAttr(element: HTMLElement, name: string, value: unknown) {
  if (value == null || value === '') {
    element.removeAttribute(name)
    return
  }
  element.setAttribute(name, String(value))
}

// The stock table view paints every unsized column with min-width: 25px and
// replaces the table width with that total. Author widths and borders stay
// on the element; 25px is only a resize-handle floor, not a column size.
export class JohkuTableView implements NodeView {
  node: ProseMirrorNode
  dom: HTMLDivElement
  table: HTMLTableElement
  colgroup: HTMLTableColElement
  contentDOM: HTMLTableSectionElement

  constructor(node: ProseMirrorNode, _cellMinWidth?: number) {
    this.node = node
    this.dom = document.createElement('div')
    this.dom.className = 'tableWrapper'
    this.table = this.dom.appendChild(document.createElement('table'))
    this.colgroup = this.table.appendChild(document.createElement('colgroup'))
    this.contentDOM = this.table.appendChild(document.createElement('tbody'))
    this.render(node)
  }

  update(node: ProseMirrorNode) {
    if (node.type !== this.node.type) {
      return false
    }
    this.node = node
    this.render(node)
    return true
  }

  ignoreMutation(mutation: ViewMutationRecord) {
    const target = mutation.target as Node
    const insideWrapper = this.dom.contains(target)
    const insideContent = this.contentDOM.contains(target)
    return insideWrapper && !insideContent
  }

  render(node: ProseMirrorNode) {
    const attrs = node.attrs
    setAttr(this.table, 'class', attrs.class)
    setAttr(this.table, 'width', attrs.width)
    setAttr(this.table, 'border', attrs.border)
    setAttr(this.table, 'cellpadding', attrs.cellpadding)
    setAttr(this.table, 'cellspacing', attrs.cellspacing)
    setAttr(this.table, 'align', attrs.align)
    setAttr(this.table, 'bgcolor', attrs.bgcolor)
    this.table.style.cssText = attrs.style || ''

    let nextCol = this.colgroup.firstChild as HTMLTableColElement | null
    const row = node.firstChild
    let totalWidth = 0
    let fixed = true
    if (row) {
      for (let i = 0; i < row.childCount; i++) {
        const { colspan, colwidth } = row.child(i).attrs as SizedCell
        const span = colspan || 1
        for (let j = 0; j < span; j++) {
          const width = columnWidth(colwidth, j)
          if (!width) {
            fixed = false
          } else {
            totalWidth += width
          }
          if (!nextCol) {
            nextCol = document.createElement('col')
            this.colgroup.appendChild(nextCol)
          }
          nextCol.style.cssText = width ? `width: ${width}px` : ''
          nextCol = nextCol.nextSibling as HTMLTableColElement | null
        }
      }
    }
    while (nextCol) {
      const after = nextCol.nextSibling
      nextCol.parentNode?.removeChild(nextCol)
      nextCol = after as HTMLTableColElement | null
    }

    if (fixed && totalWidth > 0 && !hasAuthorTableWidth(attrs)) {
      this.table.style.width = `${totalWidth}px`
      this.table.style.removeProperty('min-width')
    }
  }
}

const tableAttributes = () => ({
  style: {
    default: null,
    parseHTML: (element: HTMLElement) => styleFromTable(element),
    renderHTML: (attributes: { style?: string | null }) => attributes.style ? { style: attributes.style } : {},
  },
  class: htmlAttribute('class', sanitizeTableClass),
  width: htmlAttribute('width', sanitizeSize),
  border: htmlAttribute('border', sanitizeBorderAttr),
  cellpadding: htmlAttribute('cellpadding', sanitizeSize),
  cellspacing: htmlAttribute('cellspacing', sanitizeSize),
  align: htmlAttribute('align', sanitizeAlign),
  bgcolor: htmlAttribute('bgcolor', sanitizeColor),
})

const rowAttributes = () => ({
  style: {
    default: null,
    parseHTML: (element: HTMLElement) => sanitizeStyle(element.getAttribute('style')),
    renderHTML: (attributes: { style?: string | null }) => attributes.style ? { style: attributes.style } : {},
  },
  class: htmlAttribute('class', sanitizeTableClass),
  align: htmlAttribute('align', sanitizeAlign),
  valign: htmlAttribute('valign', sanitizeValign),
  bgcolor: htmlAttribute('bgcolor', sanitizeColor),
})

const cellAttributes = () => ({
  colwidth: {
    default: null,
    rendered: false,
    parseHTML: (element: HTMLElement) => parseColwidth(element),
  },
  style: {
    default: null,
    parseHTML: (element: HTMLElement) => styleFromCell(element),
    renderHTML: (attributes: { style?: string | null, colspan?: number, colwidth?: number[] | null }) => {
      let style = attributes.style || null
      const width = (attributes.colspan || 1) === 1 ? columnWidth(attributes.colwidth, 0) : null
      if (width && styleProperty(style, 'width') !== `${width}px`) {
        style = setStyleProperty(style, 'width', `${width}px`)
      }
      return style ? { style } : {}
    },
  },
  class: htmlAttribute('class', sanitizeTableClass),
  width: htmlAttribute('width', sanitizeSize),
  height: htmlAttribute('height', sanitizeSize),
  border: htmlAttribute('border', sanitizeBorderAttr),
  align: htmlAttribute('align', sanitizeAlign),
  valign: htmlAttribute('valign', sanitizeValign),
  bgcolor: htmlAttribute('bgcolor', sanitizeColor),
})

export const JohkuTable = Table.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      View: JohkuTableView,
    }
  },

  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      ...tableAttributes(),
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    return renderTable({
      node,
      HTMLAttributes: mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    })
  },
})

export const JohkuTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      ...rowAttributes(),
    }
  },
})

function extendCell(node: typeof TableCell) {
  return node.extend({
    addAttributes() {
      return {
        ...(this.parent?.() || {}),
        ...cellAttributes(),
      }
    },
  })
}

export const JohkuTableCell = extendCell(TableCell)
export const JohkuTableHeader = extendCell(TableHeader)
